import type { ColumnVisibilityState, OnChangeFn, SortingState } from '@tanstack/react-table';
import { type ReactNode, useMemo } from 'react';
import { ProductTableContext } from '../../contexts/product-table-context';
import type { Product } from '../../data/product';
import { type Filters, useFilteredProducts } from '../../hooks/products';
import type { StrictColumnDef } from '../tanstack-table/column';
import { ReportActions } from '../tanstack-table/report-actions';
import { TanStackTable } from '../tanstack-table/table';
import { useRouteRowSelection } from '../tanstack-table/use-route-row-selection';
import { useTableRef } from '../tanstack-table/use-table-ref';
import { PageToolbar } from '../toolbar/page-toolbar';
import { SearchWithCount } from '../toolbar/search-with-count';

/**
 * Either a fixed value, or a function of the filter snapshot that produces one.
 * `T` is never a function type here, so the two are distinguishable at runtime.
 */
type FilterDependent<T> = T | ((filters: Filters) => T);

function resolve<T>(value: FilterDependent<T>, filters: Filters): T {
  return typeof value === 'function' ? (value as (f: Filters) => T)(filters) : value;
}

type ProductReportProps = {
  /** The report's product list, from `productReportsLayout.useLoaderData()`. */
  products: Product[];
  /** Report name, shown by `ReportActions` and used for the export filename. */
  title: string;
  /** Filters the report opens with, and the set `FiltersPopover` resets to. */
  defaultFilters: Filters;
  /**
   * Column definitions. Pass a function when a column depends on a filter —
   * e.g. the "units sold" window follows `filters.soldIn`.
   *
   * Filter-dependent props receive the **debounced** filter snapshot: `query`
   * lags a keystroke by 250ms, every other field is live. That is deliberate —
   * rebuilding the column array on each keystroke would defeat the referential
   * stability `useFilteredProducts` maintains for `filteredProducts`, and
   * TanStack would rebuild the whole row model as the owner types.
   */
  columns: FilterDependent<StrictColumnDef<Product>[]>;
  /** Sort order. Omit for the table's own default. */
  sorting?: FilterDependent<SortingState>;
  /**
   * Only for a report that controls its sort state. Called with the filter
   * snapshot so the handler can see the current filter-dependent column ids.
   */
  onSortingChange?: (filters: Filters) => OnChangeFn<SortingState>;
  /** Hides sort-only columns that shouldn't be displayed. */
  columnVisibility?: ColumnVisibilityState;
  /**
   * The whole left toolbar slot, popover included — `negative-inventory`
   * renders a bare filter with no popover, and `popularity` needs a second
   * control beside one. Filter components inside read live filter state from
   * `ProductTableContext`, so only a slot deriving something *itself* from the
   * filters needs the function form.
   */
  toolbarLeft?: FilterDependent<ReactNode>;
  /** Rendered at the start of the right slot, before the search box. */
  toolbarRight?: ReactNode;
};

/**
 * The shared scaffold behind every product report: filter state, row
 * selection, the two-slot toolbar and the table. A report page supplies its
 * columns, its default filters and its toolbar contents; everything else here
 * is identical across all of them.
 */
export function ProductReport({
  products,
  title,
  defaultFilters,
  columns,
  sorting,
  onSortingChange,
  columnVisibility,
  toolbarLeft,
  toolbarRight,
}: ProductReportProps) {
  const { filters, setFilters, filteredProducts, baselineCount, debouncedFilters } =
    useFilteredProducts(products, defaultFilters);
  const { rowSelection, onRowSelectionChange, onRowClick } = useRouteRowSelection();
  const [table, onTableReady] = useTableRef<Product>();

  const resolvedColumns = useMemo(
    () => resolve(columns, debouncedFilters),
    [columns, debouncedFilters]
  );

  const resolvedSorting = useMemo(
    () => (sorting === undefined ? undefined : resolve(sorting, debouncedFilters)),
    [sorting, debouncedFilters]
  );

  const resolvedOnSortingChange = useMemo(
    () => onSortingChange?.(debouncedFilters),
    [onSortingChange, debouncedFilters]
  );

  const contextValue = useMemo(
    () => ({ filters, setFilters, filteredProducts }),
    [filters, setFilters, filteredProducts]
  );

  return (
    <>
      <ProductTableContext.Provider value={contextValue}>
        <PageToolbar
          left={toolbarLeft === undefined ? undefined : resolve(toolbarLeft, debouncedFilters)}
          right={
            <>
              {toolbarRight}
              <SearchWithCount
                quantity={filteredProducts.length}
                total={baselineCount}
                noun="products"
              />
              <ReportActions table={table} title={title} />
            </>
          }
        />
      </ProductTableContext.Provider>
      <TanStackTable
        data={filteredProducts}
        columns={resolvedColumns}
        sorting={resolvedSorting}
        onSortingChange={resolvedOnSortingChange}
        columnVisibility={columnVisibility}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        onRowClick={onRowClick}
        getRowId={(product) => product.id}
        onTableReady={onTableReady}
      />
    </>
  );
}
